import React, { useMemo, useRef, useState } from 'react'
import { Check, CornerDownRight, Film, MessageSquare, Upload } from 'lucide-react'
import { parseContentUrls, parseReviewNotes } from '@adthlete/shared-frontend'
import type { DeliverableItem } from '@adthlete/shared-frontend'
import { useAppDispatch } from '@/hooks/useRedux'
import { uploadVideoForMilestone } from '@/redux/campaignThunks'
import {
  useAddSubmissionNoteMutation,
  useGetSubmissionNotesQuery,
  useResolveSubmissionNoteMutation,
  type SubmissionNote,
} from '@/redux/collaborationsApi'
import { cn } from '@/lib/utils'
import { Eyebrow, Pill, fieldCls, linkCls, primaryCls } from './ui'

const latestOf = (m: any) =>
  [...(m?.submissions ?? [])].sort((a, b) => (b.version ?? 0) - (a.version ?? 0))[0] ?? null

const stamp = (sec: number) =>
  `${Math.floor(sec / 60)}:${String(Math.round(sec % 60)).padStart(2, '0')}`

type Draft = { file: File | null; url: string | null; caption: string; progress: number }

/**
 * Content review: one video and caption per deliverable, sent as a numbered version. Shows the
 * brand's answer per deliverable and their notes (video moments, caption edits), which the
 * athlete can reply to and tick off before sending the next version.
 */
export const ContentStep: React.FC<{
  dealId: string
  milestone: any
  deliverables: DeliverableItem[]
  brandName: string
  busy: boolean
  onSubmit: (payload: Record<string, unknown>) => Promise<unknown>
}> = ({ dealId, milestone, deliverables, brandName, busy, onSubmit }) => {
  const dispatch = useAppDispatch()
  const latest = latestOf(milestone)
  const pending = latest?.status === 'pending_review'
  const approved = latest?.status === 'approved' || milestone.status === 'approved'
  const changes = latest?.status === 'revision_requested'
  const prev = parseContentUrls(latest?.contentUrls)
  const review = parseReviewNotes(latest?.reviewNotes)

  // Deliverables the brand marked as shared with another one aren't uploaded twice
  const items = deliverables.filter((d) => !d.sameAsDeliverableId)
  const [drafts, setDrafts] = useState<Record<string, Draft>>(() =>
    Object.fromEntries(
      items.map((d) => {
        const entry: any = prev?.[d.id]
        const keepCaption = review?.[d.id]?.caption?.status === 'approved'
        return [
          d.id,
          {
            file: null,
            url: null,
            caption: keepCaption ? (entry?.caption ?? '') : '',
            progress: 0,
          },
        ]
      }),
    ),
  )
  const [note, setNote] = useState('')
  const [uploading, setUploading] = useState(false)
  const inputs = useRef<Record<string, HTMLInputElement | null>>({})

  const pick = (id: string, file: File | null) =>
    setDrafts((s) => ({ ...s, [id]: { ...s[id], file, progress: 0 } }))
  const setCaption = (id: string, caption: string) =>
    setDrafts((s) => ({ ...s, [id]: { ...s[id], caption } }))

  // A video the brand already approved can be kept for the next version
  const keepsVideo = (id: string) =>
    changes && review?.[id]?.video?.status === 'approved' && !!(prev?.[id] as any)?.video
  const ready = items.every((d) => drafts[d.id]?.file || keepsVideo(d.id))

  const send = async () => {
    setUploading(true)
    try {
      const payload: Record<string, unknown> = {}
      for (const d of items) {
        const draft = drafts[d.id]
        let video = keepsVideo(d.id) ? ((prev?.[d.id] as any)?.video ?? null) : null
        if (draft.file) {
          const up = await dispatch(
            uploadVideoForMilestone({
              campaignId: dealId,
              milestoneId: milestone.id as any,
              file: draft.file,
              onProgress: (p) => setDrafts((s) => ({ ...s, [d.id]: { ...s[d.id], progress: p } })),
            }),
          ).unwrap()
          video = up.url
        }
        payload[d.id] = { video, caption: draft.caption.trim() || null }
      }
      for (const d of deliverables.filter((x) => x.sameAsDeliverableId)) {
        payload[d.id] = { sameAsDeliverableId: d.sameAsDeliverableId }
      }
      await onSubmit({
        content_description: 'Video',
        content_urls: { deliverables: payload },
        notes: note.trim() || null,
      })
    } finally {
      setUploading(false)
    }
  }

  const label = (d: DeliverableItem, i: number) =>
    `${i + 1}. ${[d.platform, d.format].filter(Boolean).join(' ')}${d.notes ? ` · ${d.notes}` : ''}`

  // What the brand said, per deliverable
  const verdict = (id: string) => {
    const r = review?.[id]
    if (!r) return null
    return (
      <div className="mt-2 space-y-1.5 text-sm">
        {(['video', 'caption'] as const).map((k) =>
          r[k] ? (
            <p key={k} className="flex items-start gap-2">
              <Pill tone={r[k]!.status === 'approved' ? 'green' : 'amber'}>
                {k === 'video' ? 'Video' : 'Caption'}{' '}
                {r[k]!.status === 'approved' ? 'approved' : 'changes'}
              </Pill>
              {r[k]!.comment ? (
                <span className="whitespace-pre-wrap text-foreground">{r[k]!.comment}</span>
              ) : null}
            </p>
          ) : null,
        )}
      </div>
    )
  }

  if (approved || pending) {
    return (
      <div className="mt-3 space-y-3">
        <Pill tone={approved ? 'green' : 'blue'}>
          {approved ? 'Approved' : `With ${brandName}`}
          {latest?.version ? ` · v${latest.version}` : ''}
        </Pill>
        {items.map((d, i) => {
          const entry: any = prev?.[d.id]
          return (
            <div key={d.id} className="rounded-xl bg-muted p-3">
              <p className="text-sm font-semibold text-foreground">{label(d, i)}</p>
              {entry?.video ? (
                <video
                  src={entry.video}
                  controls
                  playsInline
                  className="mt-2 w-full rounded-lg bg-black"
                />
              ) : null}
              {entry?.caption ? (
                <p className="mt-2 whitespace-pre-wrap text-sm text-foreground">{entry.caption}</p>
              ) : null}
              {verdict(d.id)}
            </div>
          )
        })}
        {latest ? <ReviewNotes dealId={dealId} submission={latest} brandName={brandName} /> : null}
      </div>
    )
  }

  return (
    <div className="mt-3 space-y-3">
      {changes ? (
        <div className="rounded-xl border border-[#FCD9A8] bg-[#FFF4E5] p-3">
          <Eyebrow tone="amber">
            {brandName} asked for changes on v{latest.version}
          </Eyebrow>
          <p className="mt-1 text-sm text-foreground">
            Their notes are below. Upload a new cut where they asked, keep what they approved, and
            send it as v{(latest.version ?? 1) + 1}.
          </p>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          One video per deliverable, with the caption you'll post. {brandName} reviews each one.
        </p>
      )}

      {items.map((d, i) => {
        const draft = drafts[d.id]
        const entry: any = prev?.[d.id]
        const keep = keepsVideo(d.id)
        return (
          <div key={d.id} className="rounded-xl border border-border bg-card p-3">
            <p className="text-sm font-semibold text-foreground">{label(d, i)}</p>
            {verdict(d.id)}
            <input
              ref={(el) => {
                inputs.current[d.id] = el
              }}
              type="file"
              accept="video/*"
              className="hidden"
              onChange={(e) => pick(d.id, e.target.files?.[0] ?? null)}
            />
            {draft.file ? (
              <div className="mt-2 flex items-center gap-2 rounded-xl bg-muted px-3 py-2 text-sm">
                <Film className="h-4 w-4 shrink-0 text-[#4D7C0F]" />
                <span className="min-w-0 flex-1 truncate text-foreground">{draft.file.name}</span>
                {uploading && draft.progress > 0 ? (
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {draft.progress}%
                  </span>
                ) : (
                  <button type="button" className={linkCls} onClick={() => pick(d.id, null)}>
                    Change
                  </button>
                )}
              </div>
            ) : keep ? (
              <div className="mt-2 flex items-center gap-2 rounded-xl bg-[#F7FEE7] px-3 py-2 text-sm">
                <Check className="h-4 w-4 text-[#4D7C0F]" />
                <span className="flex-1 text-foreground">Keeping the approved video</span>
                <button
                  type="button"
                  className={linkCls}
                  onClick={() => inputs.current[d.id]?.click()}
                >
                  Replace
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => inputs.current[d.id]?.click()}
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border py-5 text-sm font-semibold text-foreground hover:border-[#C0FD72] hover:bg-[#F7FEE7]"
              >
                <Upload className="h-4 w-4" />{' '}
                {entry?.video ? 'Upload the new cut' : 'Choose a video'}
              </button>
            )}
            <textarea
              value={draft.caption}
              onChange={(e) => setCaption(d.id, e.target.value)}
              placeholder="Caption, with #ad"
              className={`${fieldCls} mt-2 min-h-20 resize-none`}
            />
          </div>
        )
      })}

      {latest ? <ReviewNotes dealId={dealId} submission={latest} brandName={brandName} /> : null}

      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder={`A note for ${brandName} (optional)`}
        className={`${fieldCls} min-h-16 resize-none`}
      />
      <button
        type="button"
        className={primaryCls}
        disabled={busy || uploading || !ready}
        onClick={send}
      >
        {uploading
          ? 'Uploading…'
          : changes
            ? `Send v${(latest.version ?? 1) + 1}`
            : 'Send for review'}
      </button>
    </div>
  )
}

/**
 * The brand's notes on a submission: video moments with a timestamp, caption edits with the
 * suggested wording, and general notes. The athlete replies and ticks off what they've handled.
 */
export const ReviewNotes: React.FC<{ dealId: string; submission: any; brandName: string }> = ({
  dealId,
  submission,
  brandName,
}) => {
  const { data } = useGetSubmissionNotesQuery(
    { campaignId: dealId, submissionId: submission.id },
    {
      skip: !submission?.id,
    },
  )
  const [reply] = useAddSubmissionNoteMutation()
  const [resolve] = useResolveSubmissionNoteMutation()
  const [replyTo, setReplyTo] = useState<string | null>(null)
  const [text, setText] = useState('')
  const notes = data?.notes ?? []
  const open = useMemo(() => notes.filter((n) => !n.isResolved).length, [notes])
  if (!notes.length) return null

  const send = async (parentId: string) => {
    if (!text.trim()) return
    await reply({ campaignId: dealId, submissionId: submission.id, text: text.trim(), parentId })
    setText('')
    setReplyTo(null)
  }

  const NoteBody: React.FC<{ n: SubmissionNote; top?: boolean }> = ({ n, top }) => (
    <div className={cn('rounded-xl p-3', n.isResolved ? 'bg-muted/60 opacity-70' : 'bg-muted')}>
      <div className="flex items-start gap-2">
        {n.target === 'video' && n.timestampSec != null ? (
          <Pill tone="blue">{stamp(n.timestampSec)}</Pill>
        ) : n.target === 'caption' ? (
          <Pill tone="amber">Caption</Pill>
        ) : (
          <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <div className="min-w-0 flex-1 text-sm">
          <p className="text-foreground">
            <span className="font-semibold">{n.author?.side === 'brand' ? brandName : 'You'}</span>{' '}
            {n.text}
          </p>
          {n.suggestion ? (
            <p className="mt-1 rounded-lg bg-[#FFFFFF] p-2 text-xs">
              <span className="text-muted-foreground line-through">{n.suggestion.from}</span>
              <br />
              <span className="font-semibold text-foreground">{n.suggestion.to}</span>
            </p>
          ) : null}
          {top ? (
            <div className="mt-1.5 flex gap-3">
              <button
                type="button"
                className={linkCls}
                onClick={() => setReplyTo(replyTo === n.id ? null : n.id)}
              >
                Reply
              </button>
              <button
                type="button"
                className="text-sm font-semibold text-muted-foreground underline underline-offset-2"
                onClick={() =>
                  resolve({
                    campaignId: dealId,
                    submissionId: submission.id,
                    noteId: n.id,
                    resolved: !n.isResolved,
                  })
                }
              >
                {n.isResolved ? 'Reopen' : 'Done'}
              </button>
            </div>
          ) : null}
        </div>
      </div>
      {n.replies?.length ? (
        <ul className="mt-2 space-y-1.5 border-l-2 border-border pl-3">
          {n.replies.map((r) => (
            <li key={r.id} className="flex items-start gap-1.5 text-sm">
              <CornerDownRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="text-foreground">
                <span className="font-semibold">
                  {r.author?.side === 'brand' ? brandName : 'You'}
                </span>{' '}
                {r.text}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {replyTo === n.id ? (
        <div className="mt-2 flex gap-2">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Reply"
            className={fieldCls}
            onKeyDown={(e) => e.key === 'Enter' && send(n.id)}
          />
          <button
            type="button"
            className="shrink-0 rounded-xl bg-[#C0FD72] px-4 text-sm font-semibold text-[#0F172A]"
            onClick={() => send(n.id)}
          >
            Send
          </button>
        </div>
      ) : null}
    </div>
  )

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Eyebrow>
          {brandName}'s notes on v{submission.version}
        </Eyebrow>
        <span className="text-xs text-muted-foreground">
          {open ? `${open} to handle` : 'All handled'}
        </span>
      </div>
      {notes.map((n) => (
        <NoteBody key={n.id} n={n} top />
      ))}
    </div>
  )
}

export default ContentStep
